import type { MfaVerifyResponse } from '@dnx/contracts';
import { Alert, Button, Field, Input, OtpInput } from '@dnx/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AuthLayout } from '../components/auth-layout.js';
import { api, ApiError } from '../lib/api.js';
import { pendingStep } from '../lib/session.js';

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const [remaining, setRemaining] = useState<number | null>(null);

  const verify = useMutation({
    mutationFn: (body: { code: string } | { recoveryCode: string }) =>
      api<MfaVerifyResponse>('/auth/mfa/verify', { method: 'POST', body }),
    onSuccess: async (res) => {
      pendingStep.set(null);
      if (res.recoveryCodesRemaining !== undefined && res.recoveryCodesRemaining <= 3) {
        setRemaining(res.recoveryCodesRemaining);
        return;
      }
      await qc.invalidateQueries({ queryKey: ['auth'] });
      void navigate({ to: '/' });
    },
    onError: (e) => {
      setCode('');
      if (e instanceof ApiError && (e.code === 'SESSION_EXPIRED' || e.code === 'UNAUTHENTICATED')) void navigate({ to: '/entrar' });
    },
  });

  if (remaining !== null) {
    return (
      <AuthLayout title="Atenção aos códigos de recuperação">
        <Alert tone="warning" title={`Restam ${remaining} códigos de recuperação`}>
          Quando recuperar o acesso ao celular, peça ao administrador para gerar novos códigos.
        </Alert>
        <Button className="mt-6" size="lg" block onClick={async () => { await qc.invalidateQueries({ queryKey: ['auth'] }); void navigate({ to: '/' }); }}>
          Entendi, continuar
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Verificação em duas etapas"
      description={mode === 'totp' ? 'Abra o aplicativo autenticador e digite o código de 6 dígitos.' : 'Use um dos códigos que você guardou ao ativar a verificação.'}
    >
      {mode === 'totp' ? (
        <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); if (code.length === 6) verify.mutate({ code }); }}>
          <Field label="Código do autenticador" error={verify.error?.message}>
            <OtpInput autoFocus value={code} disabled={verify.isPending}
              onValueChange={(v) => { setCode(v); if (v.length === 6) verify.mutate({ code: v }); }} />
          </Field>
          <Button type="submit" size="lg" block loading={verify.isPending} disabled={code.length !== 6}>Verificar</Button>
        </form>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); verify.mutate({ recoveryCode: recovery.trim() }); }}>
          <Field label="Código de recuperação" hint="Formato xxxx-xxxx. Cada código funciona uma única vez." error={verify.error?.message}>
            <Input autoFocus autoComplete="off" spellCheck={false} className="font-mono" placeholder="abcd-efgh"
              value={recovery} onChange={(e) => setRecovery(e.target.value)} />
          </Field>
          <Button type="submit" size="lg" block loading={verify.isPending} disabled={recovery.trim().length < 8}>Verificar</Button>
        </form>
      )}
      <div className="mt-6 flex items-center justify-between text-sm">
        <Button variant="link" onClick={() => { verify.reset(); setMode(mode === 'totp' ? 'recovery' : 'totp'); }}>
          {mode === 'totp' ? 'Perdi o acesso ao celular' : 'Usar o aplicativo autenticador'}
        </Button>
        <Button variant="link" className="text-fg-muted" onClick={() => void navigate({ to: '/entrar' })}>Voltar</Button>
      </div>
    </AuthLayout>
  );
}
